import { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import type { CreateTextTranslationWithTextSchema } from '@/routes/(app)/(playground)/(text-translation)/lib/schema';

interface InstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
const InstructionsDialog: FC<InstructionsDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const form = useFormContext<CreateTextTranslationWithTextSchema>();
  const { t } = useTranslation();

  const instructions = [
    'toneAndStyle',
    'narratorPersona',
    'targetAudience',
    'culturalAdaptation',
    'additionalInstructions',
  ] as const;

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('playground.textTranslation.instructions.label')}
          </DialogTitle>
          <DialogDescription>
            {t('playground.textTranslation.instructions.description')}
          </DialogDescription>
        </DialogHeader>

        {instructions.map((instruction) => (
          <FormField
            key={instruction}
            control={form.control}
            name={`instructions.${instruction}`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t(
                    `playground.textTranslation.instructions.${instruction}.label`
                  )}
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t(
                      `playground.textTranslation.instructions.${instruction}.placeholder`
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        ))}
        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleClose}>
            {t('playground.textTranslation.instructions.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InstructionsDialog;
